import { useEffect, useState } from "react";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
    Loader2,
    Search,
    Send,
    RefreshCw,
    History,
    Filter,
    X,
    AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "@/lib/api";

const ANY = "__any__";

function fmtDateTime(v) {
    if (!v) return "—";
    const s = String(v).trim();
    // Backend now stores in canonical "DD-MM-YYYY hh:mm AM/PM" — pass through.
    if (/^\d{2}-\d{2}-\d{4}\s+\d{2}:\d{2}\s+(AM|PM)$/i.test(s)) return s;
    try {
        const d = new Date(s);
        if (Number.isNaN(d.getTime())) return s;
        const dd = String(d.getDate()).padStart(2, "0");
        const mo = String(d.getMonth() + 1).padStart(2, "0");
        const yy = d.getFullYear();
        let h = d.getHours();
        const mer = h < 12 ? "AM" : "PM";
        h = h % 12 || 12;
        const mm = String(d.getMinutes()).padStart(2, "0");
        return `${dd}-${mo}-${yy} ${String(h).padStart(2, "0")}:${mm} ${mer}`;
    } catch {
        return s;
    }
}

const emptyFilters = () => ({
    search: "",
    status: ANY,
    trigger: ANY,
});

export default function WhatsAppLogs() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState(emptyFilters());
    const [refreshing, setRefreshing] = useState(false);

    const [resendTarget, setResendTarget] = useState(null);
    const [resendRegenerate, setResendRegenerate] = useState(false);
    const [resending, setResending] = useState(false);

    const fetchAll = async () => {
        try {
            const { data } = await api.get("/whatsapp-logs", {
                params: { limit: 1000 },
            });
            setLogs(data);
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const refresh = () => {
        setRefreshing(true);
        fetchAll();
    };

    const visible = logs.filter((l) => {
        if (filters.status !== ANY && l.status !== filters.status) return false;
        if (filters.trigger !== ANY && l.trigger_type !== filters.trigger)
            return false;
        const s = filters.search.trim().toLowerCase();
        if (s) {
            const hay = [
                l.name || "",
                l.phone || "",
                l.otp || "",
                l.error_message || "",
            ]
                .join(" ")
                .toLowerCase();
            if (!hay.includes(s)) return false;
        }
        return true;
    });

    const statusPill = (status) => {
        if (status === "Success") {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                    Success
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-red-50 text-red-700 border border-red-200">
                <AlertTriangle size={10} /> Failed
            </span>
        );
    };

    const openResend = (log) => {
        setResendTarget(log);
        setResendRegenerate(false);
    };

    const confirmResend = async () => {
        if (!resendTarget?.candidate_id) return;
        setResending(true);
        try {
            await api.post(`/candidates/${resendTarget.candidate_id}/send-otp`, {
                regenerate: resendRegenerate,
            });
            toast.success("WhatsApp resent");
            setResendTarget(null);
            fetchAll();
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally {
            setResending(false);
        }
    };

    return (
        <div data-testid="admin-whatsapp-logs-page">
            <AdminPageHeader
                eyebrow="Send History"
                title="WhatsApp Logs"
                description="Every WhatsApp message sent to a candidate — automatic runs, manual sends, failures. Resend any message with one click."
                actions={
                    <button
                        onClick={refresh}
                        data-testid="btn-refresh-logs"
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border border-[#0F1F38]/20 hover:border-[#0F1F38] text-[#0F1F38] transition-colors"
                        disabled={refreshing}
                    >
                        <RefreshCw
                            size={14}
                            strokeWidth={1.5}
                            className={refreshing ? "animate-spin" : ""}
                        />
                        Refresh
                    </button>
                }
            />

            <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">
                {/* Filters */}
                <div
                    data-testid="logs-filter-bar"
                    className="bg-white border border-[#0F1F38]/10 p-4 md:p-5 grid grid-cols-1 md:grid-cols-5 gap-3"
                >
                    <div className="md:col-span-2">
                        <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                            Search
                        </Label>
                        <div className="relative">
                            <Search
                                size={14}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0F1F38]/40"
                            />
                            <Input
                                data-testid="filter-search"
                                value={filters.search}
                                onChange={(e) =>
                                    setFilters({ ...filters, search: e.target.value })
                                }
                                className="pl-9"
                                placeholder="Name, phone, OTP, error…"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                            Status
                        </Label>
                        <Select
                            value={filters.status}
                            onValueChange={(v) => setFilters({ ...filters, status: v })}
                        >
                            <SelectTrigger data-testid="filter-status">
                                <SelectValue placeholder="Any" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ANY}>Any</SelectItem>
                                <SelectItem value="Success">Success</SelectItem>
                                <SelectItem value="Failed">Failed</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                            Trigger
                        </Label>
                        <Select
                            value={filters.trigger}
                            onValueChange={(v) => setFilters({ ...filters, trigger: v })}
                        >
                            <SelectTrigger data-testid="filter-trigger">
                                <SelectValue placeholder="Any" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ANY}>Any</SelectItem>
                                <SelectItem value="Manual">Manual</SelectItem>
                                <SelectItem value="Automatic">Automatic</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="md:col-span-5 flex items-center justify-end gap-2 pt-2 border-t border-[#0F1F38]/5">
                        <button
                            data-testid="btn-reset-filters"
                            onClick={() => setFilters(emptyFilters())}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-[#0F1F38]/15 text-[#0F1F38]/70 hover:border-[#0F1F38]/40"
                        >
                            <X size={12} /> Reset
                        </button>
                        <div className="text-xs text-[#4B5563] ml-2">
                            Showing <strong>{visible.length}</strong> of {logs.length}
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="mt-4 bg-white border border-[#0F1F38]/10">
                    {loading ? (
                        <div className="p-16 flex items-center justify-center text-[#4B5563]">
                            <Loader2 className="animate-spin mr-2" size={18} />
                            Loading logs…
                        </div>
                    ) : visible.length === 0 ? (
                        <div
                            data-testid="logs-empty"
                            className="p-16 text-center text-[#4B5563]"
                        >
                            <History
                                size={28}
                                className="mx-auto mb-3 text-[#0F1F38]/30"
                                strokeWidth={1.2}
                            />
                            <div className="font-medium text-[#0F1F38]">
                                No messages yet
                            </div>
                            <p className="text-sm mt-1">
                                {logs.length === 0
                                    ? "Once WhatsApps go out, every send lands here."
                                    : "No entries match the current filters."}
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table data-testid="logs-table">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Time</TableHead>
                                        <TableHead>Candidate</TableHead>
                                        <TableHead>Phone</TableHead>
                                        <TableHead>OTP</TableHead>
                                        <TableHead>Trigger</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Error</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {visible.map((l) => (
                                        <TableRow
                                            key={l.id}
                                            data-testid={`log-row-${l.id}`}
                                        >
                                            <TableCell className="text-xs whitespace-nowrap">
                                                {fmtDateTime(l.sent_time)}
                                            </TableCell>
                                            <TableCell className="font-medium">
                                                {l.name || "—"}
                                            </TableCell>
                                            <TableCell className="tabular-nums text-xs">
                                                {l.phone}
                                            </TableCell>
                                            <TableCell className="font-mono text-xs">
                                                {l.otp}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                                {l.trigger_type}
                                            </TableCell>
                                            <TableCell>{statusPill(l.status)}</TableCell>
                                            <TableCell className="text-xs text-[#4B5563] max-w-xs truncate">
                                                {l.error_message || "—"}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <button
                                                    data-testid={`btn-resend-${l.id}`}
                                                    onClick={() => openResend(l)}
                                                    disabled={!l.candidate_id}
                                                    title={
                                                        l.candidate_id
                                                            ? "Resend"
                                                            : "Candidate deleted — cannot resend"
                                                    }
                                                    className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[#0F1F38]/15 hover:border-emerald-600 hover:text-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                                >
                                                    <Send size={12} />
                                                    Resend
                                                </button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>
            </div>

            {/* Resend dialog */}
            <Dialog
                open={!!resendTarget}
                onOpenChange={(v) => !v && setResendTarget(null)}
            >
                <DialogContent data-testid="resend-dialog">
                    <DialogHeader>
                        <DialogTitle>Resend WhatsApp</DialogTitle>
                        <DialogDescription>
                            To <strong>{resendTarget?.name}</strong> at{" "}
                            {resendTarget?.phone}. The candidate&apos;s current OTP will be
                            reused unless you regenerate.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex items-center justify-between py-2 border-y border-[#0F1F38]/10 my-2">
                        <div>
                            <div className="text-sm font-medium">Regenerate OTP</div>
                            <div className="text-xs text-[#4B5563]">
                                {resendRegenerate
                                    ? "A new 6-digit OTP will be generated."
                                    : `Reuse existing OTP (${resendTarget?.otp}).`}
                            </div>
                        </div>
                        <Switch
                            data-testid="toggle-resend-regenerate"
                            checked={resendRegenerate}
                            onCheckedChange={setResendRegenerate}
                        />
                    </div>
                    <DialogFooter>
                        <button
                            onClick={() => setResendTarget(null)}
                            className="px-4 py-2 text-sm border border-[#0F1F38]/20"
                        >
                            Cancel
                        </button>
                        <button
                            data-testid="btn-confirm-resend"
                            onClick={confirmResend}
                            disabled={resending}
                            className="px-4 py-2 text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-2"
                        >
                            {resending ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <Send size={14} />
                            )}
                            Send
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
